import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useSocketEvent } from '../../../shared/socket.js';

// الخادم يعيد { items, total }؛ والمُعترض في shared/api.js يفكّه إلى مصفوفة.
function asList(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.sessions || data?.data || [];
}

export function useActiveSessions() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: async () => {
      const { data } = await api.get('/sessions/active');
      return asList(data);
    },
    refetchInterval: 30000,
    staleTime: 5000
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sessions', 'active'] });

  useSocketEvent('child:active', 'open', invalidate);
  useSocketEvent('child:active', 'exit', invalidate);
  useSocketEvent('child:active', 'invoice_added', invalidate);
  useSocketEvent('child:active', 'settled', invalidate);

  return query;
}

/** يؤخّر قيمة حتى يتوقف المستخدم عن الكتابة — طلب واحد لا طلب لكل حرف. */
export function useDebouncedValue(value, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * بحث الأطفال داخل القاعة — على قاعدة البيانات لا على الصفحة المعروضة.
 *
 * مفتاح الاستعلام يبدأ بـ ['sessions','active'] فتُبطِله نفس أحداث المقبس
 * (دخول/خروج/تسوية) التي تُبطل القائمة الكاملة، ولا يبقى طفل خرج في النتيجة.
 */
export function useActiveSearch(term) {
  const q = String(term || '').trim();
  return useQuery({
    queryKey: ['sessions', 'active', 'search', q],
    queryFn: async () => {
      const { data } = await api.get('/sessions/search', { params: { q } });
      return asList(data);
    },
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5000,
    retry: false
  });
}

export function useActiveCount() {
  const { data } = useActiveSessions();
  return Array.isArray(data) ? data.length : 0;
}
